import { useState } from 'react';
import { Edit2, Trash2, Image as ImageIcon, BarChart3, ChevronDown } from 'lucide-react';
import { machineApi } from '@/services/api';
import { ExerciseStatsPanel, ImageViewer } from '@/components/common';
import type { ExerciseStatsData } from '@/components/common/ExerciseStatsPanel';
import type { Machine } from '@/types';

interface MachineGridCardProps {
  machine: Machine;
  floorPlanName: string | null;
  onEdit: () => void;
  onDelete: () => void;
}

export const MachineGridCard = ({ machine, floorPlanName, onEdit, onDelete }: MachineGridCardProps) => {
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState<ExerciseStatsData | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [showDescription, setShowDescription] = useState(false);

  const hasDescription = !!(machine.description || machine.exerciseDescription);

  const loadStats = async () => {
    if (stats) {
      setShowStats(!showStats);
      return;
    }

    setLoadingStats(true);
    try {
      const response = await machineApi.getStats(machine.id, 20);
      if (response.success && response.data) {
        setStats(response.data);
        setShowStats(true);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Machine Photo */}
      {machine.photoUrl ? (
        <div className="bg-black cursor-pointer" onClick={() => setShowImageViewer(true)}>
          <img
            src={machine.thumbnailUrl || machine.photoUrl}
            alt={machine.name}
            className="w-full h-[200px] object-contain"
          />
        </div>
      ) : (
        <div className="bg-gray-100 dark:bg-gray-700 h-[200px] flex items-center justify-center">
          <ImageIcon className="w-12 h-12 text-gray-400" />
        </div>
      )}

      {/* Image Viewer Lightbox */}
      {machine.photoUrl && (
        <ImageViewer
          src={machine.photoUrl}
          alt={machine.name}
          isOpen={showImageViewer}
          onClose={() => setShowImageViewer(false)}
        />
      )}

      <div className="flex items-start justify-between p-2">
        <div className="flex-1">
          <div className="font-semibold text-gray-900 dark:text-gray-100">{machine.name}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{machine.type}</div>
          {floorPlanName && <div className="text-xs text-primary-600">{floorPlanName}</div>}
          {machine.location && <div className="text-xs text-gray-500 dark:text-gray-400">{machine.location}</div>}
        </div>
        <div className="flex gap-1">
          <button
            onClick={loadStats}
            className={`p-1.5 rounded-lg transition-colors ${showStats ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400' : 'text-gray-400 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="Statisztika"
          >
            {loadingStats ? (
              <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <BarChart3 className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={onEdit}
            className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Description Toggle Button */}
      {hasDescription && (
        <div className="border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={() => setShowDescription(!showDescription)}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
          >
            <span>{showDescription ? 'Leírás elrejtése' : 'Leírás'}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${showDescription ? 'rotate-180' : ''}`} />
          </button>
        </div>
      )}

      {/* Description Panel */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: showDescription ? '400px' : '0px' }}
      >
        <div className="px-3 py-2.5 bg-gradient-to-r from-blue-50 to-white dark:from-gray-750 dark:to-gray-800 border-t border-gray-100 dark:border-gray-700 space-y-2">
          {machine.description && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">Bemutató</div>
              <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">{machine.description}</p>
            </div>
          )}
          {machine.exerciseDescription && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">Gyakorlat</div>
              <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">{machine.exerciseDescription}</p>
            </div>
          )}
        </div>
      </div>

      {/* Stats Panel */}
      {showStats && stats && (
        <div className="px-2 py-2 bg-gradient-to-r from-primary-50 to-white dark:from-gray-700 dark:to-gray-800 border-t border-gray-200 dark:border-gray-600">
          <ExerciseStatsPanel
            stats={stats}
            onClose={() => setShowStats(false)}
            emptyMessage="Még nincs edzésed ezzel a géppel."
          />
        </div>
      )}
    </div>
  );
};

export default MachineGridCard;
